import { Job } from '@betterfunnel/types';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { usePersistedJsonState } from '../hooks/usePersistedState';
import { exportAnalytics } from '../services/AnalyticsService';
import { emptyConverter } from '../utils';
import { useAuthContext } from './AuthContext';
import { useSnackbarContext } from './SnackbarContext';
import { useUserContext } from './UserContext';

interface ExportAnalyticsContextProps {
  exportAnalytics: typeof exportAnalytics;
}

export const ExportAnalyticsContext =
  createContext<ExportAnalyticsContextProps | null>(null);

export function ExportAnalyticsContextProvider(props: any) {
  const { show, hide, getSnackbarById } = useSnackbarContext();
  const { firebaseUser } = useAuthContext();
  const { selectedAccount } = useUserContext();
  const [exportJobs, setExportJobs] = useState<Job[]>();
  const [hiddenJobs, setHiddenJobs] = usePersistedJsonState<string[]>(
    'hiddenExportJobs',
    [],
  );

  useEffect(() => {
    if (!firebaseUser || !selectedAccount) {
      setExportJobs([]);
      return;
    }

    const accountRef = selectedAccount.ref;
    const jobsRef = query(
      collection(accountRef, 'jobs').withConverter<Job>(emptyConverter<Job>()),
      where('type', '==', 'exportAnalytics'),
      where('userIds', 'array-contains', firebaseUser.uid),
      where('updatedAt', '>', new Date(Date.now() - 24 * 60 * 60 * 1000)), // Last 24 hours
    );

    const unsubscribe = onSnapshot(
      jobsRef,
      (snapshot) => {
        const idsSet = new Set(snapshot.docs.map((d) => d.id));
        setHiddenJobs((prev) => prev.filter((jobId) => idsSet.has(jobId)));
        setExportJobs(snapshot.docs.map((d) => d.data()));
      },
      (e) => {
        console.error('Error fetching export jobs:', e);
      },
    );

    return () => unsubscribe();
  }, [firebaseUser, selectedAccount, setHiddenJobs]);

  useEffect(() => {
    if (!exportJobs || !exportJobs.length) return;

    exportJobs.forEach((job) => {
      // Skip hidden jobs
      if (hiddenJobs.includes(job.id)) return;

      const ipSnackbar = getSnackbarById(job.id + '-inProgress');
      const completedSnackbar = getSnackbarById(job.id + '-completed');
      const failedSnackbar = getSnackbarById(job.id + '-failed');

      if (job.status === 'inProgress') {
        if (!ipSnackbar) {
          show('Export is in progress...', 'loading', {
            disableAutoClose: true,
            id: job.id + '-inProgress',
          });
        }
        hide(job.id + '-completed');
        hide(job.id + '-failed');
      } else if (job.status === 'completed') {
        if (!completedSnackbar) {
          show('Export completed successfully', 'success', {
            disableAutoClose: true,
            hideCloseButton: true,
            id: job.id + '-completed',
            buttons: [
              {
                title: 'Download',
                onClick: () => {
                  window.open(job.result?.downloadUrl, '_blank');
                },
                color: 'primary',
              },
              {
                title: 'Dismiss',
                onClick: () => {
                  setHiddenJobs((prev) => [...prev, job.id]);
                  hide(job.id + '-completed');
                },
              },
            ],
          });
        }
        hide(job.id + '-inProgress');
        hide(job.id + '-failed');
      } else if (job.status === 'failed') {
        if (!failedSnackbar) {
          show('Export job failed', 'warn', {
            disableAutoClose: true,
            id: job.id + '-failed',
          });
        }
        hide(job.id + '-inProgress');
        hide(job.id + '-completed');
        setHiddenJobs((prev) => [...prev, job.id]);
      }
    });

    // Clear completed or failed jobs
    if (exportJobs.some((job) => job.status !== 'inProgress')) {
      setExportJobs(exportJobs.filter((job) => job.status === 'inProgress'));
    }
  }, [
    exportJobs,
    getSnackbarById,
    hiddenJobs,
    hide,
    setExportJobs,
    setHiddenJobs,
    show,
  ]);

  const _exportAnalytics: typeof exportAnalytics = useCallback(
    async (...args) => {
      const snackbar = show('Exporting analytics data...', 'info', {
        disableAutoClose: true,
      });

      try {
        try {
          return await exportAnalytics(...args);
        } catch (error: any) {
          console.error('Export analytics error:', error);
          show(`Failed to export analytics: ${error.message}`, 'warn');
          throw error;
        }
      } finally {
        hide(snackbar.id);
      }
    },
    [hide, show],
  );

  return (
    <ExportAnalyticsContext.Provider
      value={{ exportAnalytics: _exportAnalytics }}
    >
      {props.children}
    </ExportAnalyticsContext.Provider>
  );
}

export function useExportAnalyticsContext() {
  const context = useContext(ExportAnalyticsContext);
  if (!context) {
    throw new Error(
      'useExportAnalyticsContext must be used within ExportAnalyticsContextProvider',
    );
  }
  return context;
}
